import { Form, Button, Card } from "react-bootstrap";
import AppLoader from 'components/general/AppLoader';
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useEffect, useState, useCallback } from "react"; // Added useCallback
import { EditorState, convertToRaw, ContentState } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";

export default function EditModal({ announcement, getAnnouncements, showEditButton = false, buttonClassName = '', onEdit, onCancel, onSave }) {
  const { user: authUser } = useSelector((x) => x.auth);

  const url = process.env.REACT_APP_URL;

  const [to, setTo] = useState(announcement.to || "");
  const [title, setTitle] = useState("");
  const [shortDescription, setShortDescription] = useState(EditorState.createEmpty());
  const [description, setDescription] = useState(EditorState.createEmpty());
  const [saveLoading, setSaveLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleCloseCard = () => setShowModal(false);


  useEffect(() => {
    if (announcement) {
      setTitle(announcement.title || "");

      // Convert shortDescription to EditorState
      const shortContentBlock = htmlToDraft(announcement.shortDescription || "");
      if (shortContentBlock) {
        const shortContentState = ContentState.createFromBlockArray(shortContentBlock.contentBlocks);
        setShortDescription(EditorState.createWithContent(shortContentState));
      }

      // Convert description to EditorState
      const contentBlock = htmlToDraft(announcement.description || "");
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        setDescription(EditorState.createWithContent(contentState));
      }
    }
  }, [announcement]);

  const handleSubmit = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      setSaveLoading(true);

      const shortDescriptionHtml = draftToHtml(convertToRaw(shortDescription.getCurrentContent()));
      const descriptionHtml = draftToHtml(convertToRaw(description.getCurrentContent()));

      // Validation for character limits
      if (title.length > 100) {
        toast.error("Title must be 100 characters or less.", { position: "bottom-left" });
        setSaveLoading(false);
        return;
      }

      if (shortDescription.getCurrentContent().getPlainText().length > 200) {
        toast.error("Short description must be 200 characters or less.", { position: "bottom-left" });
        setSaveLoading(false);
        return;
      }

      if (description.getCurrentContent().getPlainText().length > 2500) {
        toast.error("Description must be 2500 characters or less.", { position: "bottom-left" });
        setSaveLoading(false);
        return;
      }

      try {
        const response = await fetch(`${url}/announcement/${announcement._id}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authUser.token}`,
          },
          body: JSON.stringify({
            to,
            title,
            shortDescription: shortDescriptionHtml,
            description: descriptionHtml,
          }),
        });

        if (response.status === 200) {
          await response.json();
          toast.success("Announcement updated successfully", {
            position: "bottom-left",
          });
          getAnnouncements(); // Refresh the announcements list
          handleCloseCard();
        } else {
          const errorData = await response.json();
          toast.error(`Failed to update announcement: ${errorData.error || "Unknown error"}`, {
            position: "bottom-left",
          });
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to update announcement. Please try again later.", { position: "bottom-left" });
      } finally {
        setSaveLoading(false);
      }
    },
    [announcement._id, authUser.token, description, getAnnouncements, shortDescription, title, to, url]
  );

  // Add CTRL+S (or CMD+S) keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (showModal && !saveLoading) {
          handleSubmit(); // Call handleSubmit without an event object
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showModal, saveLoading, handleSubmit]);

  return (
    <>
      {showEditButton ?
        <Button
          variant="success"
          className={`mb-1 float-end ${buttonClassName}`.trim()}
          onClick={onEdit}
        >
          Edit Announcement
        </Button>
        :
        <Card
          className='text-center announcements-form-card'
          data-bs-theme={'dark'}
        >
          {/* <Modal show={showModal} onHide={handleCloseModal} size="lg" data-bs-theme="dark" style={{ top: "2%" }}> */}
          <Card.Header className="announcements-form-header">
            <Card.Title className="mb-0">Edit Announcement</Card.Title>
          </Card.Header>
          <Card.Body className="announcements-form-body">
            <Form onSubmit={handleSubmit}>
              <Form.Group controlId="formTo" className="mb-3">
                <Form.Label>Audience</Form.Label>
                <Form.Select
                  aria-label="To"
                  className="formInput"
                  name="to"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                >
                  <option value="" disabled>Select Audience</option>
                  <option value="Officer">Officers</option>
                  <option value="Member">Members</option>
                  <option value="Guest">Guests</option>
                  <option value="Public">Public</option>
                </Form.Select>
              </Form.Group>

              <Form.Group controlId="formTitle" className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter announcement title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Form.Group>

              <Form.Group controlId="formShortDescription" className="mb-3">
                <Form.Label>Short Description</Form.Label>
                <Editor
                  editorState={shortDescription}
                  wrapperClassName="demo-wrapper"
                  toolbarClassName="toolbar-class"
                  editorClassName="editor-class"
                  onEditorStateChange={setShortDescription}
                  placeholder="Enter the short description..."
                  toolbar={{
                    options: ['inline', 'blockType', 'list', 'textAlign', 'link'],
                    inline: {
                      options: ['bold', 'italic', 'underline'],
                    },
                    blockType: {
                      inDropdown: false,
                      options: ['Normal', 'H4', 'H5', 'H6'],
                    },
                    fontSize: {
                      visible: false,
                      options: [8, 9, 10, 11, 12, 14, 18],
                    },
                    list: {
                      inDropdown: false,
                      options: ['unordered', 'ordered'],
                    },
                    textAlign: {
                      inDropdown: false,
                    },
                    link: {
                      inDropdown: false,
                    },
                    embedded: {
                      inDropdown: false,
                    },
                  }}
                />
              </Form.Group>

              <Form.Group controlId="formDescription" className="mb-3">
                <Form.Label>Description</Form.Label>
                <Editor
                  editorState={description}
                  wrapperClassName="demo-wrapper"
                  toolbarClassName="toolbar-class"
                  editorClassName="editor-class"
                  onEditorStateChange={setDescription}
                  placeholder="Enter full description..."
                  toolbar={{
                    options: ['inline', 'blockType', 'list', 'textAlign', 'link'],
                    inline: {
                      options: ['bold', 'italic', 'underline'],
                    },
                    blockType: {
                      inDropdown: false,
                      options: ['Normal', 'H4', 'H5', 'H6'],
                    },
                    fontSize: {
                      visible: false,
                      options: [8, 9, 10, 11, 12, 14, 18],
                    },
                    list: {
                      inDropdown: false,
                      options: ['unordered', 'ordered'],
                    },
                    textAlign: {
                      inDropdown: false,
                    },
                    link: {
                      inDropdown: false,
                    },
                    embedded: {
                      inDropdown: false,
                    },
                  }}
                />
              </Form.Group>
            </Form>
          </Card.Body>
          <Card.Footer className="announcements-form-footer">
            {saveLoading ? <AppLoader size="sm" inline /> :
              <>
                <Button
                  variant="primary"
                  className="announcements-action-btn announcements-action-btn--edit"
                  onClick={handleSubmit}
                  disabled={saveLoading}
                >
                  Update Announcement
                </Button>
                <Button
                  variant="secondary"
                  className='ms-2 announcements-action-btn announcements-action-btn--neutral'
                  onClick={onCancel || handleCloseCard}
                >
                  Cancel
                </Button>
              </>}
          </Card.Footer>
        </Card>
      }
    </>
  );
}